import { inject, Injectable } from '@angular/core';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { FeatureFlags } from '@rma/dashboard/generic/util/feature-flags';
import { combineLatest, map, Observable } from 'rxjs';
import { COLLECT_GROUP_FEATURES, CollectGroupFeature, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  ManageGroupFeature,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class IndividualSalesSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);
  private readonly profileContextService = inject(DashboardProfileContextService);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      enableDatafeeds: this.config.getLiveFeature(FeatureFlags.EnableDataFeeds, false),
      googlePermissions: this.config.getGoogleConfig$,
      marketReport: this.config.getMarketReportConfig$,
      showIntergations: this.config.getLiveFeature(FeatureFlags.DashboardEnableIntegrations, false),
      showCuratedSocial: this.config.showSocialStudioLink$,
      showSocialMediaManager: this.config.showSocialMediaManager$,
      showGoogle: this.config.showGoogle$,
      showPromoter: this.config.showPromoter$,
      isAssistant: this.profileContextService.isAssistant$,
      showBillingAndSubs: this.config.showBillingAndSubs$,
    }).pipe(
      map(
        ({
          enableDatafeeds,
          googlePermissions,
          marketReport,
          showIntergations,
          showCuratedSocial,
          showSocialMediaManager,
          showGoogle,
          showPromoter,
          isAssistant,
          showBillingAndSubs,
        }) => {
          const { baseUrl } = this.environment;
          const { hasMarketReportAccess, hideMarketReport } = marketReport;
          const userType: NavigationUserType = 'agent';

          return [
            createNavigationGroup([getProfile()]),
            createNavigationGroup([getOverview()]),
            this.getCollectItems(userType),
            this.getShareItems(
              userType,
              showIntergations,
              hideMarketReport,
              hasMarketReportAccess,
              googlePermissions,
              baseUrl,
              showCuratedSocial,
              showSocialMediaManager,
              showPromoter,
              showGoogle,
            ),
            this.getMonitoringItems(),
            this.getManageItems(baseUrl, enableDatafeeds, isAssistant, showBillingAndSubs),
          ].filter((group) => group.items.length);
        },
      ),
    );
  }

  private getCollectItems(userType: NavigationUserType) {
    const notAvailable: CollectGroupFeature[] = ['leasing', 'nps', 'pending'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));
    const items = getCollectGroupItems(features, userType);
    return createNavigationGroup(items, 'collect');
  }

  private getShareItems(
    userType: NavigationUserType,
    showIntergations: boolean,
    hideMarketReport: boolean,
    hasMarketReportAccess: boolean,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
    showCuratedSocial: boolean,
    showSocialMediaManager: boolean,
    showPromoter: boolean,
    showGoogle: boolean,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(
      filterShareItems(showIntergations, hideMarketReport, true, showCuratedSocial, showSocialMediaManager, showPromoter, showGoogle),
    );
    const items = getShareGroupItems(features, userType, hasMarketReportAccess, googlePermissions, baseUrl);
    return createNavigationGroup(items, 'share');
  }

  private getMonitoringItems() {
    return createNavigationGroup(getMonitorGroupItems([...MONITORING_GROUP_FEATURES]), 'monitor');
  }

  private getManageItems(baseUrl: string, enableDatafeeds: boolean, isAssistant: boolean, showBillingAndSubs: boolean): NavigationGroup {
    return createNavigationGroup(
      [
        getSettingsGroupItems(this.getSettingsItems(enableDatafeeds), baseUrl),
        ...getManageGroupItems(this.getManageGroupItems(isAssistant, showBillingAndSubs)),
      ],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = [];
    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));
    return items;
  }

  private getManageGroupItems(isAssistant: boolean, showBillingAndSubs: boolean): ManageGroupFeature[] {
    const notAvailable: ManageGroupFeature[] = ['teamAndAdmins'];

    if (isAssistant || !showBillingAndSubs) {
      notAvailable.push('billingAndSubs');
    }

    const items = MANAGE_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));
    return items;
  }
}
